import React, { useEffect, useState } from 'react'
import restApi from '../../../api'
import { bindActionCreators } from 'redux'
import { type ConnectedProps, connect } from 'react-redux'
import { EjentaForm } from '../../elements/EjentaForm'
import { profileViewSlice, fetchPagingNotes } from '../../../store/profileView'
import { Alert, Box, Button, Card, CardContent, Grid, TextareaAutosize, Typography } from '@mui/material'
import LoadingIndicator from '../../elements/LoadingIndicator'
import Pagination from '../../elements/Pagination'
import NoteRow from '../../elements/Notes/NoteRow'
import { NOTES_PAGE_SIZE } from '../../../constants/constants'
import { type IMemberSummary } from '../../../interfaces/member-summary.interface'
import { type INote } from '../../../interfaces/note.interface'
import { type RootState, type AppDispatch } from '../../..'

interface INotesProps extends PropsFromRedux {
  memberObj: IMemberSummary
}

function NotesView (props: INotesProps): JSX.Element {
  const localStorageID = `newNote_${props.memberObj.user.id as string}_${props.clinicianId as string}`
  const [newNote, setNewNote] = useState(window.localStorage.getItem(localStorageID) ?? '')
  const [noteCount, setNoteCount] = useState(props.profileViewStore?.selectedAlert?.notes?.length as number)

  useEffect(() => {
    props.fetchPagingNotes()
  }, [])

  useEffect(() => {
    window.localStorage.setItem(localStorageID, newNote)
  }, [newNote])

  useEffect(() => {
    const selectedAlertNotesCount = props.profileViewStore?.selectedAlert?.notes?.length
    if (!selectedAlertNotesCount) return

    if (selectedAlertNotesCount > noteCount) {
      props.fetchPagingNotes()
    }

    setNoteCount(selectedAlertNotesCount)
  }, [props.profileViewStore?.selectedAlert])

  const handleNoteInputChange = (e: any): void => {
    setNewNote(e.target.value.slice(0, 1000))
  }

  const handleAddNoteSubmit = (e: any): void => {
    e.preventDefault()
    props.addNote({
      memberId: props.memberObj.user.id,
      clinicianId: props.clinicianId,
      providerContent: newNote.trim()
    })
    setNewNote('')
  }

  const onHandlePagePrev = (): void => {
    props.profileViewActions.pagePrev()
    props.fetchPagingNotes()
  }

  const onHandlePageNext = (): void => {
    props.profileViewActions.pageNext()
    props.fetchPagingNotes()
  }

  const renderNotes = (): JSX.Element [] | JSX.Element => {
    const notes = props.profileViewStore?.currentPage as INote []
    if (notes) {
      return notes.map((noteDatum: INote) => {
        return (
          <NoteRow
            key={noteDatum.id as string}
            noteData={noteDatum}
          />
        )
      })
    }
    return <Alert severity='error'>Failed to retrieve notes for this member</Alert>
  }

  const renderAddNote = (): JSX.Element => {
    const numCharsInNote = newNote.length

    return (
      <EjentaForm onSubmit={handleAddNoteSubmit}>
        <Card sx={{ mt: 1, mb: 1 }}>
          <CardContent sx={{ padding: '.5rem !important', backgroundColor: '#fafafa' }}>
            <Grid container columnSpacing={2}>
              <Grid item sm={10}>
                <Box sx={{ width: '100%' }}>
                  <TextareaAutosize
                    minRows={2}
                    style={{ border: '1px solid #cacaca', width: '-webkit-fill-available', borderRadius: '5px', fontFamily: 'inherit', fontSize: '1.1rem', padding: '1rem' }}
                    data-testid='profile-note__add-note-textarea'
                    className='ejenta-textarea'
                    onChange={handleNoteInputChange}
                    value={newNote}
                    placeholder='Add a note...'
                  />
                </Box>
                <Box sx={{ width: '100%', textAlign: 'end' }} data-testid='profile-note__add-note-count'>
                  <Typography variant='caption'> {numCharsInNote} / 1000 characters </Typography>
                </Box>
              </Grid>
              <Grid item sm={2} className='profile-note__add-note-action'>
                {newNote?.trim() &&
                  <Button variant='contained' type='submit' data-testid='profile-note__add-note-button' onClick={handleAddNoteSubmit}>
                    Add note
                  </Button>}
                {(!newNote?.trim()) &&
                  <Button color='secondary' disabled type='submit'> Add note </Button>}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </EjentaForm>
    )
  }

  const renderNotesView = (): JSX.Element => {
    if (props.profileViewStore?.isLoading) return <LoadingIndicator sidebarVisible />

    return (
      <div>
        {renderAddNote()}
        {renderNotes()}
        {(props.profileViewStore?.currentPage && props.profileViewStore?.currentPage.length > 0) &&
          <Pagination
            pageStart={((props.profileViewStore.page - 1) * NOTES_PAGE_SIZE) + 1}
            pageEnd={props.profileViewStore.page * NOTES_PAGE_SIZE}
            itemCount={props.profileViewStore.totalCount}
            handlePagePrev={onHandlePagePrev}
            handlePageNext={onHandlePageNext}
            setPage={(_page: number) => {}}
            page={1}
            custom={false}
          />}
      </div>
    )
  }

  return renderNotesView()
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function mapState (state: RootState) {
  return {
    profileViewStore: state.profileView,
    clinicianId: state.userSession.user?.id
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function mapDispatch (dispatch: AppDispatch) {
  return {
    profileViewActions: bindActionCreators(profileViewSlice.actions, dispatch),
    fetchPagingNotes: bindActionCreators(fetchPagingNotes, dispatch),
    addNote: ({ memberId, clinicianId, providerContent }) => {
      dispatch(restApi.actions.addNote({ id: memberId }, {
        body: JSON.stringify({
          author_id: clinicianId,
          provider_content: providerContent
        })
      }))
    }
  }
}

const connector = connect(mapState, mapDispatch)
type PropsFromRedux = ConnectedProps<typeof connector>
export default connector(NotesView)
